import { FileText, Home, Briefcase, MessageSquare, AlertTriangle, Calendar, ArrowRight, Store } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SERVICES_DATA } from "../data";

interface ServicesProps {
  onOpenModal: (type: "surat" | "aspirasi" | "login" | "umkm") => void;
}

export default function Services({ onOpenModal }: ServicesProps) {
  const navigate = useNavigate();
  
  // Icon mapper inside pale green background circle container
  const renderIcon = (iconName: string) => {
    const iconClass = "w-6 h-6 text-primary stroke-[2]";
    switch (iconName) {
      case "FileText":
        return <FileText className={iconClass} />;
      case "Home":
        return <Home className={iconClass} />;
      case "Store":
        return <Store className={iconClass} />;
      case "Briefcase":
        return <Briefcase className={iconClass} />;
      case "MessageCircleCode":
        return <MessageSquare className={iconClass} />;
      case "AlertTriangle":
        return <AlertTriangle className={iconClass} />;
      case "Calendar":
        return <Calendar className={iconClass} />;
      default:
        return <FileText className={iconClass} />;
    }
  };

  const handleCardClick = (serviceId: string) => {
    const pathMap: Record<string, string> = {
      "srv-pengantar": "/surat-pengantar",
      "srv-domisili": "/surat-domisili",
      "srv-usaha": "/umkm",
      "srv-aspirasi": "/aspirasi-warga",
      "srv-pengaduan": "/pengaduan",
      "srv-agenda": "/informasi"
    };

    if (pathMap[serviceId]) {
      navigate(pathMap[serviceId]);
    }
  };

  return (
    <section id="layanan" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Title structure */}
        <div className="text-center space-y-2 mb-14">
          <h2 className="text-3xl font-extrabold text-[#005c32] tracking-tight font-sans uppercase">
            LAYANAN CEPAT
          </h2>
          <div className="w-24 h-1 bg-[#ffd22e] mx-auto rounded-full" />
          <p className="text-sm text-slate-500 font-medium max-w-md mx-auto pt-1">
            Akses cepat layanan administrasi surat-menyurat dan penyampaian aduan warga RW 14
          </p>
        </div>

        {/* 6 Grid columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {SERVICES_DATA.map((service) => (
            <div 
              key={service.id}
              onClick={() => handleCardClick(service.id)}
              className="group bg-slate-50/70 hover:bg-white rounded-2xl p-5 border border-slate-100 hover:border-[#005c32]/20 shadow-sm hover:shadow-lg transition-all duration-300 text-center cursor-pointer flex flex-col items-center justify-between min-h-[180px]"
            >
              <div className="space-y-3.5 flex flex-col items-center w-full">
                {/* Circular pale-green container as requested */}
                <div className="w-13 h-13 rounded-full bg-[#005c32]/5 text-[#005c32] flex items-center justify-center transition-all duration-300 group-hover:bg-[#005c32] group-hover:text-white group-hover:scale-105 shadow-sm">
                  {renderIcon(service.iconName)}
                </div>

                <div className="space-y-1">
                  <h3 className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight leading-snug group-hover:text-[#005c32] transition-colors">
                    {service.label}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-medium leading-relaxed max-w-[120px] mx-auto hidden sm:block">
                    {service.id === "srv-agenda" ? "Agenda terdekat" : (service.id === "srv-usaha" ? "Katalog & Pendaftaran" : "Verifikasi cepat")}
                  </p>
                </div>
              </div>

              {/* Action indicator arrow */}
              <div className="text-xs font-bold text-[#005c32] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 mt-2 font-sans">
                <span>Ajukan</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>

            </div>
          ))}
        </div>

        {/* Center link button */}
        <div className="flex justify-center mt-12">
          <button 
            onClick={() => navigate("/surat-pengantar")}
            className="px-6 py-3.5 bg-[#005c32] hover:bg-[#008f4c] text-white font-bold rounded-full text-xs tracking-wider flex items-center gap-2 shadow-md hover:shadow-lg transition-all cursor-pointer group"
          >
            <span>LIHAT SEMUA LAYANAN</span>
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 text-[#ffd22e]" />
          </button>
        </div>

      </div>
    </section>
  );
}
